import type { ComponentProps, ReactNode } from "react";

import { Text } from "@prophero-ds/cx-react";

import { useMediaQuery } from "@/hooks/useMediaQuery";
import { MEDIA_QUERY } from "@/utils/mediaQueries";

import BackButton from "./BackButton";

interface ScreenHeaderWithBackProps {
  title: string;
  /**
   * Plain string (wrapped in the default body2/secondary styling) or a
   * pre-composed node (e.g. multiple `Text` fragments) for screens that need
   * to emphasize part of the description.
   */
  description: ReactNode;
  onBack?: () => void;
  canGoBack?: boolean;
  backAriaLabel: string;
  backButtonId?: string;
  containerClassName?: string;
  titleClassName?: string;
  descriptionClassName?: string;
  /**
   * Visual role for the title. Defaults to h1 (32px) on desktop and h2 (24px)
   * below 1024px.
   */
  titleVariant?: ComponentProps<typeof Text>["variant"];
  /** Classes for the title and description stack. */
  headingClassName?: string;
}

function ScreenHeaderWithBack({
  title,
  description,
  onBack,
  canGoBack,
  backAriaLabel,
  backButtonId,
  containerClassName,
  titleClassName,
  descriptionClassName,
  titleVariant,
  headingClassName = "flex flex-col gap-2",
}: ScreenHeaderWithBackProps) {
  const isDesktop = useMediaQuery(MEDIA_QUERY.desktop);
  const resolvedTitleVariant = titleVariant ?? (isDesktop ? "h1" : "h2");
  const showBackButton = canGoBack !== false && onBack;

  return (
    <div
      className={`flex flex-col items-start gap-4 text-left${
        containerClassName ? ` ${containerClassName}` : ""
      }`}
    >
      {showBackButton && (
        <BackButton
          id={backButtonId}
          onClick={onBack!}
          ariaLabel={backAriaLabel}
        />
      )}

      <div className={headingClassName}>
        <Text as="h1" variant={resolvedTitleVariant} className={titleClassName}>
          {title}
        </Text>
        {typeof description === "string" ? (
          <Text
            as="p"
            variant="body2"
            color="secondary"
            className={descriptionClassName}
          >
            {description}
          </Text>
        ) : (
          <div className={descriptionClassName}>{description}</div>
        )}
      </div>
    </div>
  );
}

export default ScreenHeaderWithBack;
